import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { formatDate, formatCurrency, getLicenseStatusColor, isLicenseExpired, getAppIcon, getAppColor } from "@/lib/utils";
import { License, Application } from "@shared/schema";
import { CreditCard, Calendar, Info, User, Building2 } from "lucide-react";

type ViewEmployeeLicensesDialogProps = {
  employeeId: number | null;
  employeeName?: string;
  isOpen: boolean;
  onClose: () => void;
};

export default function ViewEmployeeLicensesDialog({
  employeeId,
  employeeName = "Employee",
  isOpen,
  onClose,
}: ViewEmployeeLicensesDialogProps) {
  const { data: applications = [] } = useQuery<Application[]>({
    queryKey: ['/api/applications'],
    enabled: isOpen,
  });

  // Query licenses assigned to this employee
  const { data: assignedLicenses = [], isLoading } = useQuery<License[]>({
    queryKey: ['/api/employees', employeeId, 'licenses'],
    queryFn: async () => {
      if (!employeeId) throw new Error("Employee ID is required");
      try {
        const res = await fetch(`/api/employees/${employeeId}/licenses`);
        if (!res.ok) return [];
        return res.json();
      } catch (error) {
        console.error("Error fetching employee licenses:", error);
        return [];
      }
    },
    enabled: isOpen && employeeId !== null,
  });

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Assigned Licenses for {employeeName}</DialogTitle>
          <DialogDescription>
            View all licenses assigned to this employee
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4 mt-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : assignedLicenses.length === 0 ? (
          <div className="text-center p-6 bg-slate-50 rounded-md">
            <p className="text-muted-foreground">No licenses assigned to this employee.</p>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {assignedLicenses.map((license) => {
              const app = applications.find((a) => a.id === license.applicationId);
              return (
              <div key={license.id} className="bg-slate-50 rounded-md p-4">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    <div className={`h-9 w-9 rounded ${getAppColor((app as any)?.logo ?? null)} flex items-center justify-center flex-shrink-0`}>
                      <i className={`${getAppIcon((app as any)?.logo ?? null)} text-sm`}></i>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{app?.name ?? 'Unknown App'}</p>
                      <h3 className="font-semibold text-base leading-tight">{license.name}</h3>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {license.billingCycle} • Cost: {formatCurrency(license.cost)}
                      </p>
                    </div>
                  </div>
                  {(() => {
                    const isLifetime = license.billingCycle === 'Lifetime' || license.billingCycle === 'One-Time';
                    const isExpired = !isLifetime && license.status !== 'discontinued' && isLicenseExpired(license.renewalDate);
                    const displayStatus = isExpired ? 'expired' : (license.status || 'unknown');
                    const displayLabel = displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1);
                    return <Badge className={getLicenseStatusColor(displayStatus)}>{displayLabel}</Badge>;
                  })()}
                </div>
                
                <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>Start: {formatDate(license.startDate || null)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>Renewal: {formatDate(license.renewalDate || null)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span>Total Seats: {license.totalSeats}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>Assigned Seats: {license.assignedSeats}</span>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}